import { forwardRef } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslationApp } from "../../lib/i18next";
import { BUTTON_DISCARD } from "../../i18n/keysTranslations";
import { getLanguageFormatted } from "../../utils/string";
import { resolveLocalizedGlobalNotification } from "../../utils/globalNotifications";
import Button from "../Buttons/Button";
import TaskAlertCard from "./TaskAlertCard";

const buttonSx = {
  height: 32,
  fontSize: "0.75rem",
  minWidth: 100,
};

const GlobalNotificationAlertItem = forwardRef(({ alert, onClose }, ref) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslationApp();
  const language = getLanguageFormatted(i18n.language || "en");
  const localized = resolveLocalizedGlobalNotification(alert, language);
  const hasLink = Boolean(localized.link);

  const onNavigateToLink = () => {
    if (!localized.link) {
      return;
    }

    // Always keep only path + params and navigate inside the current host, so
    // white label users never get sent to the original (Adle) domain.
    try {
      const url = new URL(localized.link, window.location.origin);
      navigate(`${url.pathname}${url.search}${url.hash}`);
    } catch {
      return;
    }
  };

  const onAlertClick = (event) => {
    if (event.target.closest("button")) {
      return;
    }
    onNavigateToLink();
  };

  const onDiscard = (event) => {
    event.stopPropagation();
    if (typeof onClose === "function") {
      onClose(alert.id);
    }
  };

  const onCtaClick = (event) => {
    event.stopPropagation();
    onNavigateToLink();
  };

  const hasCta = Boolean(localized.cta) && hasLink;
  const hasDiscard = typeof onClose === "function";

  return (
    <TaskAlertCard
      ref={ref}
      status="success"
      timestamp={alert.timestamp ?? alert.createdAt ?? null}
      label={localized.title}
      onClick={hasLink ? onAlertClick : undefined}
      cursor={hasLink ? "pointer" : "default"}
      showProgressBar={false}
      showProgressPercent={false}
      sx={{ borderRadius: 3 }}
      details={
        localized.body ? (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.75rem",
              color: "text.secondary",
              whiteSpace: "pre-line",
              wordBreak: "break-word",
            }}
          >
            {localized.body}
          </Typography>
        ) : null
      }
      actions={
        hasCta || hasDiscard ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            {hasDiscard ? (
              <Button
                onClick={onDiscard}
                size="small"
                color="secondary"
                sx={buttonSx}
              >
                {t(BUTTON_DISCARD)}
              </Button>
            ) : null}
            {hasCta ? (
              <Button
                onClick={onCtaClick}
                size="small"
                color="primary"
                sx={buttonSx}
              >
                {localized.cta}
              </Button>
            ) : null}
          </Box>
        ) : null
      }
    />
  );
});

GlobalNotificationAlertItem.displayName = "GlobalNotificationAlertItem";

export default GlobalNotificationAlertItem;
